
import { Button } from "src/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "src/components/ui/dropdown-menu";
import { MetricAggregateSourceData, MetricData } from "../../types/metric-universal-display-card.types";
import { ArrowDown, ArrowUp, ChevronDown } from "lucide-react";
import { formatPercentage } from "../metric-percent-chart/utils";
import { ReportMetrics } from "src/redux/admin/reporting/reporting.types";
import { useEffect, useState } from "react";
import { calculatePercentChange } from "../../utils/calculated-values.utils";

export interface MetricUniversalButtonProps {
  slot: number;
  activeMetrics: MetricData[];
  color: string;
  data: MetricAggregateSourceData;
  onMetricChange: (metric: string) => void;
  onToggle: () => void;
  enabled?: boolean;
  selectedMetric: string;
  metricData?: ReportMetrics;
}

const METRICS = ['Clicks', 'Impressions', 'Conversions', 'Spend', 'CPA', 'CPC', 'CPM', 'CTR'];


const MetricUniversalButton: React.FC<MetricUniversalButtonProps> = ({ color, data, onToggle, onMetricChange, enabled, selectedMetric, activeMetrics, slot, metricData }) => {
  const [percentChange, setPercentChange] = useState<number>(0);

  useEffect(() => {
    if (metricData) {
      const selector = selectedMetric.toLowerCase();

      setPercentChange(calculatePercentChange(
        Number(metricData.current[selector]) ?? 0,
        Number(metricData.past[selector]) ?? 0
      ));
    }
  }, [metricData, selectedMetric]);

  const formatCurrency = (amount: number): string => {
    if (amount >= 100000) { // 6+ digits
      if (amount >= 1000000) {
        return `$${(amount / 1000000).toFixed(1).replace('.0', '')}M`;
      }
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (amount: number): string => {
    if (amount >= 100000) { // 6+ digits
      if (amount >= 1000000) {
        return `${(amount / 1000000).toFixed(1).replace('.0', '')}M`;
      }
      return `${(amount / 1000).toFixed(0)}K`;
    }
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getMetricValue = (): string => {
    switch (selectedMetric) {
      case 'Clicks':
        return formatNumber(Number(data?.clicks) || 0);
      case 'Spend':
        return formatCurrency((Number(data?.spend) || 0));
      case 'Impressions':
        return formatNumber(Number(data?.impressions) || 0);
      case 'Conversions':
        return formatNumber(Number(data?.conversions) || 0);
      case 'CPA':
        return formatCurrency(Number(data?.cpa) || 0);
      case 'CPC':
        return formatCurrency(Number(data?.cpc) || 0);
      case 'CPM':
        return formatCurrency(Number(data?.cpm) || 0);
      case 'CTR':
        return formatNumber(Number(data?.ctr) || 0);
      case 'Video Completes':
        return formatNumber(Number(data?.video_completes) || 0);
      case 'Video Starts':
        return formatNumber(Number(data?.video_starts) || 0);
      default:
        return '0';
    }
  };

  const handleMetricChange = (e: React.MouseEvent<HTMLDivElement>, metric: string) => {
    e.preventDefault();
    onMetricChange(metric);
  };

  const handleOnToggle = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    onToggle();
  };

  return (
    <div
      onClick={(e) => handleOnToggle(e)}
      className={`cursor-pointer p-6 pt-4 pr-1 relative
        ${!enabled ? `${color} text-white` : 'bg-white text-black border-b border-gray-800'}`}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between"
        onClick={(e) => e.stopPropagation()} // Prevent bubbling to parent toggle
      >
        <DropdownMenu>
          <DropdownMenuTrigger
            onClick={(e) => e.stopPropagation()}
            asChild
          >
            <Button
              className="pl-0"
              variant="ghost"
              size="med"
            >
              <ChevronDown className="w-4 h-4" />
              {selectedMetric}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {METRICS.filter(metric => !activeMetrics.find(m => m.name === metric)).map(metric => (
              <DropdownMenuItem
                key={metric + slot}
                onClick={(e) => handleMetricChange(e, metric)}
              >
                {metric}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Value Displayed Below */}
      <div className="text-3xl">
        { getMetricValue() }
      </div>
      { metricData && (
        <div className="flex items-center rounded-full absolute top-2 right-2 text-xs text-white py-1 px-2">
          {percentChange > 0 && (
            <ArrowUp className={`w-3 h-3 text-green-600 mr-1 font-medium bg-white rounded-full`} />
          )}
          {percentChange < 0 && (
            <ArrowDown className={`w-3 h-3 text-red-600 mr-1 font-medium bg-white rounded-full`} />
          )}
          {formatPercentage(percentChange)}
        </div>
      )}
    </div>
  );
};

export default MetricUniversalButton;